import { UseFormReturn } from "react-hook-form";
import FormSection from "../form-fields/FormSection";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { VisaPathway } from "../VisaPathwaySelection";

interface PersonalInfoProps {
  form: UseFormReturn<any>;
  visaPathway: VisaPathway;
}

const PersonalInfo = ({ form }: PersonalInfoProps) => {
  return (
    <FormSection
      title="Personal Information"
      description="Please provide your basic information to begin the assessment."
    >
      <div className="space-y-6">
        <FormField
          control={form.control}
          name="personalInfo.fullName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Full Name *</FormLabel>
              <FormControl>
                <Input {...field} placeholder="Enter your full name" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="personalInfo.phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Phone Number *</FormLabel>
              <FormControl>
                <Input {...field} placeholder="+1234567890" />
              </FormControl>
              <FormDescription>Include country code (e.g., +1 for US)</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="personalInfo.email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Professional Email *</FormLabel>
              <FormControl>
                <Input {...field} type="email" placeholder="Enter your email" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="personalInfo.targetRoles"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Target Roles/Industries *</FormLabel>
              <FormControl>
                <Input {...field} placeholder="e.g., Software Engineer, Data Scientist" />
              </FormControl>
              <FormDescription>Specify your desired roles or industries</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="personalInfo.portfolioUrl"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Portfolio URL</FormLabel>
              <FormControl>
                <Input {...field} type="url" placeholder="https://your-portfolio.com" />
              </FormControl>
              <FormDescription>Optional - Link to your work portfolio</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="personalInfo.linkedinUrl"
          render={({ field }) => (
            <FormItem>
              <FormLabel>LinkedIn Profile *</FormLabel>
              <FormControl>
                <Input {...field} type="url" placeholder="https://linkedin.com/in/your-profile" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="personalInfo.additionalInfo"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Additional Information</FormLabel>
              <FormControl>
                <Textarea 
                  {...field} 
                  placeholder="Any additional information you'd like to share"
                  className="min-h-[100px]"
                />
              </FormControl>
              <FormDescription>Maximum 500 characters</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="personalInfo.dataConsent"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>
                  I consent to the processing of my personal data *
                </FormLabel>
                <FormDescription>
                  Your data will be processed in accordance with our privacy policy
                </FormDescription>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </FormSection>
  );
};

export default PersonalInfo;